import React, { useState, useEffect } from 'react';
import { 
  Card, Button, Space, message, Tag, Row, Col, Typography, Divider, Table, Descriptions 
} from 'antd';
import { ArrowLeftOutlined, EditOutlined, DollarOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  fetchServiceOrder,
  type ServiceOrderDto,
  type ServiceOrderLineDto
} from '../services/serviceOrderService';
import { useBreakpoint } from '../hooks/useBreakpoint';

const { Title, Text } = Typography;

const ViewServiceOrder: React.FC = () => {
  const isMobile = useBreakpoint();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const orderId = id ? parseInt(id) : null;

  const [order, setOrder] = useState<ServiceOrderDto | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (orderId) {
      loadOrder();
    }
  }, [orderId]);

  const loadOrder = async () => {
    if (!orderId) return;
    try {
      setLoading(true);
      const data = await fetchServiceOrder(orderId);
      setOrder(data);
    } catch (error) {
      message.error('Servis fişi yüklenirken hata oluştu');
      navigate('/service-orders');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: number) => {
    switch (status) {
      case 1: return 'blue';
      case 2: return 'orange';
      case 3: return 'purple';
      case 4: return 'green';
      case 5: return 'cyan';
      default: return 'default';
    }
  };

  const isCompletedOrDelivered = (order?.status === 4 || order?.status === 5) ?? false;
  const openCollection = () => {
    if (!order) return;
    const base = window.location.pathname.replace(/\/service-orders\/\d+.*$/, '') || '';
    window.open(`${base}/service-orders/${order.id}/collection`, '_blank', 'noopener,noreferrer');
  };

  const lineColumns = [
    {
      title: 'Satır No',
      dataIndex: 'lineNo',
      key: 'lineNo',
      width: 80,
    },
    {
      title: 'Tip',
      dataIndex: 'itemTypeText',
      key: 'itemTypeText',
      width: 100,
      render: (text: string, record: ServiceOrderLineDto) => (
        <Tag color={record.itemType === 1 ? 'blue' : 'green'}>
          {text}
        </Tag>
      ),
    },
    {
      title: 'Kalem',
      key: 'itemName',
      render: (_: any, record: ServiceOrderLineDto) => {
        if (record.itemType === 1) {
          return record.serviceItemName || '-';
        } else {
          return `${record.productCode || ''} ${record.productName || ''}`.trim() || '-';
        }
      },
    },
    {
      title: 'Teknisyen',
      dataIndex: 'technicianUserName',
      key: 'technicianUserName',
      render: (name: string) => name || '-',
    },
    {
      title: 'Miktar',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 100,
      align: 'right' as const,
    },
    {
      title: 'Birim Fiyat',
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      width: 120,
      align: 'right' as const,
      render: (price: number) => `${price.toFixed(2)} ₺`,
    },
    {
      title: 'Toplam',
      dataIndex: 'totalPrice',
      key: 'totalPrice',
      width: 120,
      align: 'right' as const,
      render: (price: number) => <strong>{price.toFixed(2)} ₺</strong>,
    },
    {
      title: 'Notlar',
      dataIndex: 'notes',
      key: 'notes',
      render: (notes: string) => notes || '-',
    },
  ];

  if (loading) {
    return <div>Yükleniyor...</div>;
  }

  if (!order) {
    return <div>Servis fişi bulunamadı</div>;
  }

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Space>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/service-customers')}
          >
            Geri
          </Button>
          <Title level={2} style={{ margin: 0 }}>
            Servis Fişi Detayı
          </Title>
        </Space>
        <Button
          type="primary"
          icon={<EditOutlined />}
          onClick={() => navigate(`/service-orders/${order.id}`)}
        >
          Düzenle
        </Button>
      </div>

      <Card>
        <Descriptions title="Fiş Bilgileri" bordered column={2}>
          <Descriptions.Item label="Fiş No">
            <Text strong>{order.orderNo}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="Durum">
            <Tag color={getStatusColor(order.status)}>{order.statusText}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Müşteri">
            {order.customerName}
          </Descriptions.Item>
          <Descriptions.Item label="Araç">
            {order.vehiclePlate} {order.vehicleInfo ? `(${order.vehicleInfo})` : ''}
          </Descriptions.Item>
          <Descriptions.Item label="Tarih">
            {dayjs(order.orderDate).format('DD.MM.YYYY HH:mm')}
          </Descriptions.Item>
          <Descriptions.Item label="Sorumlu Personel">
            {order.responsibleUserName || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Giriş Km">
            {order.entryKm?.toLocaleString('tr-TR') || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Çıkış Km">
            {order.exitKm?.toLocaleString('tr-TR') || '-'}
          </Descriptions.Item>
          {order.deliveredAt && (
            <>
              <Descriptions.Item label="Teslim Tarihi">
                {dayjs(order.deliveredAt).format('DD.MM.YYYY HH:mm')}
              </Descriptions.Item>
              <Descriptions.Item label="Araç bizde kaldı">
                {Math.max(0, dayjs(order.deliveredAt).diff(dayjs(order.orderDate), 'day'))} gün
              </Descriptions.Item>
            </>
          )}
          <Descriptions.Item label="Toplam Tutar" span={2}>
            <Text strong style={{ fontSize: 18 }}>
              {order.totalAmount.toFixed(2)} ₺
            </Text>
          </Descriptions.Item>
          {isCompletedOrDelivered && (
            <Descriptions.Item span={2}>
              <Button
                type="primary"
                size="large"
                icon={<DollarOutlined />}
                onClick={openCollection}
                style={{ minWidth: 260, height: 56, fontSize: 16 }}
              >
                Tahsilat Yap / Ödeme Al
              </Button>
            </Descriptions.Item>
          )}
          {order.notes && (
            <Descriptions.Item label="Notlar" span={2}>
              {order.notes}
            </Descriptions.Item>
          )}
        </Descriptions>
      </Card>

      <Card title="Fiş Satırları" style={{ marginTop: 16 }}>
        <Table
          columns={lineColumns}
          dataSource={order.lines || []}
          rowKey="id"
          pagination={false}
          scroll={{ x: 600 }}
          summary={(pageData) => {
            const total = pageData.reduce((sum, record) => sum + (record.totalPrice || 0), 0);
            return (
              <Table.Summary fixed>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={6} align="right">
                    <Text strong>Genel Toplam:</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="right">
                    <Text strong style={{ fontSize: 16 }}>
                      {total.toFixed(2)} ₺
                    </Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2} />
                </Table.Summary.Row>
              </Table.Summary>
            );
          }}
        />
      </Card>
    </div>
  );
};

export default ViewServiceOrder;
